import {
    EpsonAlign,
    EpsonBarcode,
    EpsonColor,
    EpsonCut,
    EpsonDrawer,
    EpsonFont,
    EpsonHRI,
    EpsonLevel,
    EpsonPulse,
    EpsonSymbol,
    NFSendResult,
    NFThermalPrinter,
    PrintSettings
} from 'src/app/shared/model/nf-thermal-printer.model';

export interface NFThermalPrinterConfig {
    columns?: number;
    connectionType?: 'sp' | 'ts';
    codepageMapping?: string;
    eReceiptMode?: boolean;
    lineSpacing?: number;
    splitDocumentsOnCut?: boolean;
    printCallback?: (data: NFPrintCallbackData) => Promise<void>;
}

export interface NFPrintCallbackData {
    documents?: string[]
}

export interface NFThermalPrinterNetSettings {
    ipAddress: string;
    port: number;
    connection?: 'sp' | 'ts';
}

export abstract class NFThermalDriverBase implements NFThermalPrinter {
    protected networkSettings: NFThermalPrinterNetSettings;
    protected printSettings: PrintSettings;
    protected lastPrintSettings: PrintSettings;
    protected driverConfig: NFThermalPrinterConfig;

    constructor(ipAddress: string, port: number, configuration?: NFThermalPrinterConfig) {
        this.networkSettings = {
            ipAddress: ipAddress,
            port: port,
            connection: configuration?.connectionType
        };

        // Default style settings
        this.printSettings = {
            dw: false,
            dh: false,
            reverse: false,
            height: 1,
            width: 1,
            ul: false,
            em: false,
            linespc: configuration?.lineSpacing ?? 80,
            color: EpsonColor.COLOR_1,
            align: EpsonAlign.LEFT
        };

        this.lastPrintSettings = { ...this.printSettings };
        this.driverConfig = configuration ?? {};
    }

    /**
     * Sets the text to double width and/or height.
     * @param {boolean} [dw] - If true, sets double width.
     * @param {boolean} [dh] - If true, sets double height.
     */
    public addTextDouble(dw?: boolean, dh?: boolean): void {
        this.printSettings.dw = typeof dw === 'boolean' ? dw : this.printSettings.dw;
        this.printSettings.dh = typeof dh === 'boolean' ? dh : this.printSettings.dh;
    }

    /**
     * Sets the text size.
     * @param {number} [w] - The width of the text (1-8).
     * @param {number} [h] - The height of the text (1-8).
     */
    public addTextSize(w?: number, h?: number): void {
        this.printSettings.width = w && Number.isInteger(w) && (w >= 1 && w <= 8) ? w : this.printSettings.width;
        this.printSettings.height = h && Number.isInteger(h) && (h >= 1 && h <= 8) ? h : this.printSettings.height;
    }

    /**
     * Sets the text alignment.
     * @param {EpsonAlign} align - The alignment to be set.
     */
    public addTextAlign(align: EpsonAlign): void {
        if (Object.values(EpsonAlign).includes(align)) {
            this.printSettings.align = align;
        }
    }

    /**
     * Sets the line spacing.
     * @param {number} linespc - The line spacing value.
     */
    public addTextLineSpace(linespc: number): void {
        this.printSettings.linespc = linespc;
    }

    /**
     * Sets the text style (reverse, underline, emphasis, color).
     * @param {boolean} [reverse] - If true, sets reverse text.
     * @param {boolean} [ul] - If true, sets underline.
     * @param {boolean} [em] - If true, sets emphasis.
     * @param {EpsonColor} [color] - The color of the text.
     */
    public addTextStyle(reverse?: boolean, ul?: boolean, em?: boolean, color?: EpsonColor): void {
        this.printSettings.reverse = typeof reverse === 'boolean' ? reverse : this.printSettings.reverse;
        this.printSettings.ul = typeof ul === 'boolean' ? ul : this.printSettings.ul;
        this.printSettings.em = typeof em === 'boolean' ? em : this.printSettings.em;
        this.printSettings.color = (Object.values(EpsonColor).includes(color!) ? color : EpsonColor.COLOR_1) as EpsonColor;
    }

    abstract addBarcode(data: string, type: EpsonBarcode, hri?: EpsonHRI, font?: EpsonFont, width?: number, height?: number): void;
    abstract addCut(type?: EpsonCut): void;
    abstract addLogo(key1: number, key2: number): void;
    abstract addPulse(drawer?: EpsonDrawer, time?: EpsonPulse): void;
    abstract addText(text: string): void;
    abstract addSymbol(data: string, type: EpsonSymbol, level?: EpsonLevel, width?: number, height?: number, size?: number): void;
    abstract send(): Promise<NFSendResult>;

    /**
     * Attempts to connect to a printer and returns its connection status.
     * @param {string} ip - The IP address of the printer.
     * @param {number} port - The port number of the printer.
     * @returns {Promise<{ip: string; connected: boolean}>} An object containing the printer IP and connection status
     */
    public static async connectAttempt(ip: string, port: number): Promise<{ ip: string; connected: boolean }> {
        // This method will be implemented in subclasses.
        // For now, it provides a base structure.
        return { ip: ip, connected: false };
    }

    /**
     * Placeholder for display text functionality.
     * Subclasses can override or ignore as needed.
     * @param {any} printer - Object containing printer connection info.
     * @param {string[]} textLines - Array of text lines to display.
     * @returns {Promise<void>} A promise that resolves when the text is sent successfully.
     */
    public static async displayText?(printer: any, textLines: string[]): Promise<void>;
}